import React, { useEffect } from "react";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Cookies from "js-cookie";

import "../../../src/theme.scss";
import "../../assets/styles/common-styles.scss";
import { Link, NavLink, Navigate, useNavigate } from "react-router-dom";
import { useLoginPage } from "../../hooks/useLoginPage";
import {
  setLastAPITriggerTime,
  setUserObj,
} from "../../store/slices/commonSlice";
import { useAppDispatch } from "../../store";
import { useSnackbar } from "../../hooks/useSnackbar";
import FormLayout from "../FormLayout";
import { decodeToken } from "../../utils/decodeToken";
import { userLoginSchema } from "../../config/schemas/loginSchema";
import useLoginFields from "./useLoginFields";
import DynamicForm from "../../components/common/DynamicForm";

const LoginPage: React.FunctionComponent<any> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showSuccess, handleError } = useSnackbar();
  const { loginFields } = useLoginFields();
  type FormData = z.infer<typeof userLoginSchema>;
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: zodResolver(userLoginSchema),
  });

  const {
    loginUser,
    loginUserIsSuccess,
    loginUserData,
    loginUserIsError,
    loginUserError,
  } = useLoginPage();

  const submit = (response: FormData) => {
    loginUser({
      data: {
        username: response.email,
        password: response.password,
      },
    });
  };

  useEffect(() => {
    if (loginUserIsSuccess && loginUserData) {
      const { tokenExpiryPeriodInSeconds, userDetails } = decodeToken(
        loginUserData.access_token
      );
      Cookies.set("accessToken", loginUserData.access_token, {
        expires: tokenExpiryPeriodInSeconds / (24 * 60 * 60),
      });
      Cookies.set("refreshToken", loginUserData.refresh_token, {
        expires: 15 / (24 * 60),
      });
      dispatch(setLastAPITriggerTime(Date.now()));
      dispatch(setUserObj(userDetails));
      reset();
      showSuccess("Login Successful", "user-login-success");
      navigate("/");
    }
  }, [loginUserIsSuccess]);

  useEffect(() => {
    if (loginUserIsError) {
      const errorMsg: any = loginUserError;
      handleError(errorMsg);
    }
  }, [loginUserIsError]);

  const onInvalid = (errors: any) => console.debug(errors);

  return Cookies.get("accessToken") ? (
    <Navigate to="/" />
  ) : (
    <FormLayout title="Sign in">
      <Box sx={{ mt: 1 }}>
        <form onSubmit={handleSubmit(submit, onInvalid)}>
          <DynamicForm
            fieldsJson={loginFields}
            errors={errors}
            register={register}
            control={control}
          />
          <Grid container alignItems="center">
            <Grid item mt={1}>
              <Link to="/forgotPassword">Forgot password?</Link>
            </Grid>
          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            className="formLoginBtn"
            size="large"
            id="login"
          >
            Log In
          </Button>
        </form>
        <Grid container justifyContent="center">
          <Grid item>
            <NavLink to="/register">
              {"Don't have an account? Register here"}
            </NavLink>
          </Grid>
        </Grid>
      </Box>
    </FormLayout>
  );
};

export default LoginPage;
